import { Box, DialogContentText, List, Stack } from "@mui/material";
import { ForwardedRef, forwardRef, useImperativeHandle, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { eLynxAppIDEnum } from "../../common/enums/eLynxAppIDEnum";
import { createUrlFromRouteDetail } from "../../common/utilities/CreateUrlFromRouteDetail";
import { splitFavoriteDescription } from "../../common/utilities/SplitFavoriteDescription";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useDeleteUserFavorite } from "../../queries/user-favorites/UseDeleteUserFavorite";
import { useUserFavorites } from "../../queries/user-favorites/UseUserFavorites";
import { useValidateUserFavorite } from "../../queries/user-favorites/UseValidateUserFavorite";
import { routesByViewID } from "../../routes/routes";
import { IUIUserFavoriteModel } from "../../services/ui-personalization-data-service/Models";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from "../no-data/NoData";
import UserFavoritesManagerDialog, { bindUserFavoritesManagerDialog, useUserFavoritesManagerDialogState } from "../user-favorites-manager-dialog/UserFavoritesManagerDialog";
import { UserFavoritesListItem } from "./UserFavoritesListItem";
import { NavigationSource } from "../../common/enums/NavigationSource";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";

export type UserFavoritesListProps = {
    enabled: boolean;
    onClick?: (item: IUIUserFavoriteModel) => void;
};

export type UserFavoritesListRefType = {
    manageFavorites: () => void;
};

function UserFavoritesList({
    enabled,
    onClick
}: UserFavoritesListProps, ref?: ForwardedRef<UserFavoritesListRefType>): JSX.Element {

    const navigateTo = useNavigateTo();
    const { confirm, acknowledge } = useConfirmation();
    const { t } = useTranslation();
    const userFavoritesManagerDialog = useUserFavoritesManagerDialogState();

    const {
        data,
        status
    } = useQueryNotifications(useUserFavorites)({
        enabled: enabled
    });

    const { mutateAsync: validateUserFavorite } = useQueryNotifications(useValidateUserFavorite)({});
    const { mutateAsync: deleteUserFavorite } = useQueryNotifications(useDeleteUserFavorite)({
        onSuccess: (data) => {
            const { viewDescription, objectDescription } = splitFavoriteDescription(data.deletedDescription);
            acknowledge(
                <Stack direction="column">
                    <Box>
                        {viewDescription}
                    </Box>
                    <Box>
                        {objectDescription}
                    </Box>
                    <Box sx={{ marginTop: 1 }}>
                        {t("userFavorites.removedAcknowledgement")}
                    </Box>
                </Stack>
            );
        }
    });

    useImperativeHandle<UserFavoritesListRefType, UserFavoritesListRefType>(
        ref,
        () => ({
            manageFavorites: onManageFavorites
        })
    );

    const userFavorites = data ?? new Array<IUIUserFavoriteModel>();
    const v2HomeFavorite = data?.find(f => f.isHomeUrl && f.eLynxAppID === eLynxAppIDEnum.eLynxApp2022);

    return (
        <>
            <LoadingZone isLoading={status === "loading"}>
                {status === "success" && userFavorites.length === 0 &&
                    <NoData text="No favorites are available" />
                }

                <List
                    dense
                    sx={{
                        paddingTop: 0,
                        flex: "1 1 auto",
                        overflowY: "auto",
                        overflowX: "hidden"
                    }}>
                    {userFavorites.map(favorite =>
                        <UserFavoritesListItem
                            key={favorite.id}
                            item={{
                                ...favorite,
                                isHomeUrl: favorite.isHomeUrl &&
                                    ((favorite.eLynxAppID === eLynxAppIDEnum.eLynxApp && v2HomeFavorite === undefined) ||
                                        (favorite.eLynxAppID === eLynxAppIDEnum.eLynxApp2022))
                            }}
                            onClick={onFavoriteClick}
                        />
                    )}
                </List>
            </LoadingZone>
            <UserFavoritesManagerDialog {...bindUserFavoritesManagerDialog(userFavoritesManagerDialog)} />
        </>
    );

    async function onFavoriteClick(item: IUIUserFavoriteModel, shiftKey: boolean, ctrlKey: boolean) {

        const routeDetail = item.routeDetail;

        // Make sure we actually have a route matching the supplied view id
        const route = routesByViewID.get(routeDetail?.uiView);

        if (routeDetail && route) {


            // console.log(`Favorite -> ${item.routeDetail?.uiView}: ${route.name}`);
            // console.log(JSON.stringify(routeDetail));

            // Check to see if this favorite is still valid.
            // i.e. The referenced object, configuration, etc are still available
            const response = await validateUserFavorite({ favoriteID: item.id });

            if (response.isValid) {

                // Convert the favorite route details into a navigable url and state
                const {url, state} = createUrlFromRouteDetail(routeDetail, NavigationSource.Favorite);

                let options: NavigateToOptions = {
                    state: state
                };

                if (shiftKey || ctrlKey) {
                    options.newViewType = shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
                }
    
                // Go there
                //navigate(url, {state});
                navigateTo(url, options);

                if (onClick) {
                    onClick(item);
                }

                //console.log(`Navigate to ${url}`);

            } else {
                // console.log(`Invalid favorite: ${response.failureMessage}`);

                const { viewDescription, objectDescription } = splitFavoriteDescription(item.description);

                const result = await confirm({
                    dialogTitle: t("userFavorites.removeConfirmation.invalidTitle"),
                    confirmationText: "",
                    showConfirmationText: true,
                    confirmationBodyComponent:
                        <>
                            <DialogContentText>
                                {viewDescription}
                            </DialogContentText>
                            <DialogContentText>
                                {objectDescription}
                            </DialogContentText>
                            <DialogContentText sx={{ marginTop: 2 }}>
                                {response.failureMessage}
                            </DialogContentText>
                            <DialogContentText sx={{ marginTop: 2 }}>
                                {t("userFavorites.removeConfirmation.confirmationText")}
                            </DialogContentText>
                        </>,
                    showYesButton: true,
                    yesButtonText: "Yes, remove",
                    showNoButton: true,
                    noButtonText: "No, cancel",
                    showCloseButton: false
                });

                if (result === "yes") {
                    deleteUserFavorite(item);
                }
            }

        } else {
            // console.log(`Favorite -> ${item.routeDetail?.uiView}: No matching view`);

            const { viewDescription, objectDescription } = splitFavoriteDescription(item.description);

            const result = await confirm({
                dialogTitle: t("userFavorites.removeConfirmation.unavailableTitle"),
                confirmationText: "",
                showConfirmationText: true,
                confirmationBodyComponent:
                    <>
                        <DialogContentText>
                            {viewDescription}
                        </DialogContentText>
                        <DialogContentText>
                            {objectDescription}
                        </DialogContentText>
                        <DialogContentText sx={{ marginTop: 2 }}>
                            {t("userFavorites.removeConfirmation.unavailableExplanation")}
                        </DialogContentText>
                        <DialogContentText sx={{ marginTop: 2 }}>
                            {t("userFavorites.removeConfirmation.confirmationText")}
                        </DialogContentText>
                    </>,
                showYesButton: true,
                yesButtonText: "Yes, remove",
                showNoButton: true,
                noButtonText: "No, cancel",
                showCloseButton: false
            });

            if (result === "yes") {
                deleteUserFavorite(item);
            }
        }
    }

    function onManageFavorites() {
        userFavoritesManagerDialog.open();
    }
}

export default forwardRef(UserFavoritesList);


